import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CoachButton, Eyebrow } from '../components/primitives';
import { usePublicConfig } from '../lib/queries';
import { storedRef } from '../lib/ref';
import { useIsDesktop } from '../lib/use-is-desktop';

const SLIDES = [
  {
    eyebrow: 'Manifest',
    title: 'Get clear on what you want.',
    body: 'Turn your dreams into meaningful goals, personalized affirmations, and daily intentions.',
  },
  {
    eyebrow: 'Align',
    title: 'Understand your energy and mindset.',
    body: 'Explore personalized Vedic guidance and supportive daily practices.',
  },
  {
    eyebrow: 'Act',
    title: 'Know exactly what to do today.',
    body: 'Your success coach turns your goals into focused daily actions.',
  },
  {
    eyebrow: 'Succeed',
    title: 'Build momentum you can measure.',
    body: 'Track your actions, streaks, and progress over time.',
  },
] as const;

const HEADLINE = 'Create the life you want with your personal success coach';
const INTRO =
  'Clarify your goals, align your daily practices, take meaningful action, and track your progress, all in one personalized success system.';

/** SC-02 welcome, then the SC-03 benefits carousel. Desktop lays the four benefits beside the hero instead. */
export function WelcomePage() {
  const desktop = useIsDesktop();
  const [slide, setSlide] = useState(-1);
  const ref = storedRef();
  const signupHref = ref ? `/signup?ref=${encodeURIComponent(ref)}` : '/signup';
  const current = slide >= 0 ? SLIDES[slide] : null;
  // The courses are open to visitors here: a way in before any account.
  const browse = usePublicConfig().data?.lmsPublicCatalog ? (
    <Link to="/learn/browse" className="eyebrow hover:text-ink">
      Browse our courses
    </Link>
  ) : null;

  if (desktop) {
    return (
      <div className="grid min-h-screen grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        <div className="flex flex-col justify-between px-12 pb-16 pt-10 xl:px-24">
          <p className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
            Miracles Protocol
          </p>
          <div className="max-w-[640px] py-12">
            <Eyebrow className="mb-5">Manifest. Align. Act. Succeed.</Eyebrow>
            <h1 className="text-balance font-display text-[3rem] font-semibold leading-[1.04] tracking-[-0.015em] text-ink xl:text-[4rem]">
              {HEADLINE}
            </h1>
            <p className="mt-7 max-w-[520px] text-[1.0625rem] leading-[1.65] text-ink/70">
              {INTRO}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to={signupHref}
                className="inline-flex min-h-12 items-center justify-center bg-gold px-9 font-mono text-[0.68rem] font-medium uppercase tracking-[0.14em] text-ink hover:bg-[#c29d4c]"
              >
                Get started
              </Link>
              <Link
                to="/login"
                className="inline-flex min-h-12 items-center justify-center border border-[rgba(16,32,26,0.22)] px-8 font-mono text-[0.68rem] font-medium uppercase tracking-[0.14em] text-ink hover:border-[rgba(16,32,26,0.5)]"
              >
                Log in
              </Link>
            </div>
            {browse && <p className="mt-6">{browse}</p>}
          </div>
          <div aria-hidden className="h-px" />
        </div>
        <ol className="grid grid-rows-4 divide-y divide-border border-l border-border">
          {SLIDES.map((s, i) => (
            <li
              key={s.eyebrow}
              className="flex flex-col justify-center gap-2.5 px-10 py-8 xl:px-[72px]"
            >
              <Eyebrow>
                0{i + 1} · {s.eyebrow}
              </Eyebrow>
              <p className="font-display text-[1.625rem] font-semibold leading-[1.2] text-ink">
                {s.title}
              </p>
              <p className="max-w-[26rem] text-[0.95rem] leading-relaxed text-ink/70">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-5 pb-10 pt-8">
      <p className="font-display text-lg font-semibold text-ink">Miracles Protocol</p>
      {!current ? (
        <>
          <div className="flex flex-1 flex-col justify-center">
            <Eyebrow className="mb-4">Manifest. Align. Act. Succeed.</Eyebrow>
            <h1 className="font-display text-[2.4rem] font-semibold leading-[1.05] text-ink">
              {HEADLINE}
            </h1>
            <p className="mt-5 text-[0.98rem] leading-relaxed text-ink/70">{INTRO}</p>
          </div>
          <div className="space-y-3">
            <CoachButton kind="gold" block onClick={() => setSlide(0)}>
              Get started
            </CoachButton>
            <Link
              to="/login"
              className="flex min-h-11 w-full items-center justify-center border border-[rgba(16,32,26,0.22)] font-mono text-[0.68rem] uppercase tracking-[0.14em] text-ink hover:border-[rgba(16,32,26,0.5)]"
            >
              Log in
            </Link>
            {browse && <p className="pt-2 text-center">{browse}</p>}
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-1 flex-col justify-center" aria-live="polite">
            <Eyebrow className="mb-4">{current.eyebrow}</Eyebrow>
            <h1 className="font-display text-[2.2rem] font-semibold leading-[1.08] text-ink">
              {current.title}
            </h1>
            <p className="mt-4 text-[0.98rem] leading-relaxed text-ink/70">{current.body}</p>
          </div>
          <div className="mb-6 flex justify-center gap-2" aria-hidden>
            {SLIDES.map((s, i) => (
              <span
                key={s.eyebrow}
                className={`h-1.5 w-1.5 rounded-full ${i === slide ? 'bg-verde' : 'bg-[rgba(16,32,26,0.25)]'}`}
              />
            ))}
          </div>
          {slide < SLIDES.length - 1 ? (
            <div className="flex items-center justify-between">
              <Link to={signupHref} className="eyebrow hover:text-ink">
                Skip
              </Link>
              <CoachButton onClick={() => setSlide(slide + 1)}>Next</CoachButton>
            </div>
          ) : (
            <Link
              to={signupHref}
              className="flex min-h-11 w-full items-center justify-center bg-gold font-mono text-[0.68rem] uppercase tracking-[0.14em] text-ink hover:bg-[#c29d4c]"
            >
              Create my Success Plan
            </Link>
          )}
        </>
      )}
    </div>
  );
}
